import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Tabs, Table, Button, Tag, Tooltip, Icon, Modal, DatePicker, Input, Select, Checkbox, Progress } from 'antd';
import moment from 'moment';
import { Chart, Geom, Axis, Tooltip as ChartTooltip, Coord, Label } from 'bizcharts';
import DataSet from '@antv/data-set';
import { StyledCard, KPICard, SectionTitle } from './QualityDashboard.styles';

const { RangePicker } = DatePicker;
const { Option } = Select;

const LISTINGS_METRIC_LABELS = ['Listings in "Good" status', 'Listings in "Warning" status', 'Listings at risk', 'Suspended Listings'];

// Same listings data as Demo 3 (abbreviated - reuse structure)
const listingsData = [
  {
    key: '1',
    listingName: 'Gentle Rain Drive',
    avgScore: 4.7,
    reviewCount: 70,
    qualityStatus: 'Warning',
    qualityStatusColor: '#fa8c16',
    reviewIssues: 2,
    csViolations: 2,
    issuesTooltip: '1. Reservation ID: 5038374672\n- Accuracy: Needs maintenance\n- Cleanliness: Noticeable smell',
    modalReviewIssues: [{ reservationId: '5038374672', issues: ['Accuracy: Needs maintenance', 'Cleanliness: Noticeable smell'] }],
    modalViolations: [{ reservationId: '5038374672', violations: ['Communication: Host encouraged cancellation'] }]
  },
  {
    key: '2',
    listingName: 'Paradise Road',
    avgScore: 4.6,
    reviewCount: 290,
    qualityStatus: 'Warning',
    qualityStatusColor: '#fa8c16',
    reviewIssues: 2,
    csViolations: 2,
    issuesTooltip: '1. Reservation ID: 5503837467\n- Check-in: Unclear instructions',
    modalReviewIssues: [{ reservationId: '5503837467', issues: ['Check-in: Unclear instructions'] }],
    modalViolations: []
  },
  {
    key: '3',
    listingName: 'House in Nois',
    avgScore: 5.0,
    reviewCount: 802,
    qualityStatus: 'Educate',
    qualityStatusColor: '#52c41a',
    reviewIssues: 1,
    csViolations: 1,
    issuesTooltip: '',
    modalReviewIssues: [],
    modalViolations: []
  },
  {
    key: '4',
    listingName: 'Retreat',
    avgScore: 4.3,
    reviewCount: 262,
    qualityStatus: 'Suspended',
    qualityStatusColor: '#ff4d4f',
    reviewIssues: 2,
    csViolations: 3,
    issuesTooltip: '',
    modalReviewIssues: [],
    modalViolations: []
  }
];

// Demo 4 Reviews tab: category cards (screenshot 1)
const reviewCategories = [
  { key: 'cleanliness', label: 'Cleanliness', icon: 'home', rating: 4.44, greenPct: 83, redPct: 17 },
  { key: 'arrival', label: 'Arrival', icon: 'key', rating: 4.52, greenPct: 94, redPct: 6 },
  { key: 'accuracy', label: 'Accuracy', icon: 'check-circle', rating: 4.5, greenPct: 90, redPct: 10 },
  { key: 'communication', label: 'Communication', icon: 'message', rating: 4.45, greenPct: 88, redPct: 12 },
  { key: 'location', label: 'Location', icon: 'environment', rating: 4.64, greenPct: 95, redPct: 5 },
  { key: 'quality', label: 'Quality', icon: 'tag', rating: 4.32, greenPct: 85, redPct: 15 }
];

// Review tags by category (screenshot 2 - when "Show review tags" is on)
const reviewTagsByCategory = {
  cleanliness: {
    total: 7019,
    positive: [
      { tag: 'Free of clutter', count: 2074 },
      { tag: 'Spotless furniture & linens', count: 1908 },
      { tag: 'Squeaky-clean bathroom', count: 1640 },
      { tag: 'Pristine kitchen', count: 1457 }
    ],
    negative: [{ tag: 'Dirty or dusty', count: 428 }, { tag: 'Stains', count: 270 }, { tag: 'Not clean', count: 156 }]
  },
  arrival: {
    total: 11894,
    positive: [
      { tag: 'Clear instructions', count: 2631 },
      { tag: 'Responsive host', count: 2394 },
      { tag: 'Easy to find', count: 2039 },
      { tag: 'Easy to get inside', count: 1912 },
      { tag: 'Flexible check-in', count: 1746 },
      { tag: 'Felt right at home', count: 1172 }
    ],
    negative: [{ tag: 'Had to wait', count: 279 }, { tag: 'Confusing instructions', count: 198 }]
  },
  accuracy: {
    total: 7041,
    positive: [
      { tag: 'Looked like the photos', count: 2797 },
      { tag: 'Matched the description', count: 2543 },
      { tag: 'Had listed amenities & services', count: 1701 }
    ],
    negative: [{ tag: 'Needs maintenance', count: 278 }, { tag: "Didn't match the photos", count: 124 }, { tag: 'Unexpected noise', count: 116 }]
  },
  communication: {
    total: 5892,
    positive: [{ tag: 'Quick responses', count: 2103 }, { tag: 'Helpful', count: 1892 }],
    negative: [{ tag: 'Slow to respond', count: 412 }]
  },
  location: {
    total: 6521,
    positive: [{ tag: 'Great location', count: 3201 }, { tag: 'Easy to find', count: 1982 }],
    negative: [{ tag: 'Noisy area', count: 201 }]
  },
  quality: {
    total: 5120,
    positive: [{ tag: 'Good value', count: 1892 }, { tag: 'As described', count: 1654 }],
    negative: [{ tag: 'Overpriced', count: 387 }]
  }
};

const violationsData = [
  { label: 'Accuracy', icon: 'check-circle', count: 48 },
  { label: 'Arrival', icon: 'key', count: 12 },
  { label: 'Cleanliness', icon: 'home', count: 32 },
  { label: 'Communication', icon: 'message', count: 11 },
  { label: 'Location', icon: 'environment', count: 1 }
];

const accommodationStatuses = [
  { label: 'Good', count: 0, color: '#52c41a' },
  { label: 'Educate', count: 57, color: '#faad14' },
  { label: 'Warn', count: 200, color: '#fa8c16' },
  { label: 'Probation', count: 72, color: '#fa541c' },
  { label: 'Additional warn', count: 9, color: '#fa8c16' },
  { label: 'Pending removal', count: 5, color: '#ff4d4f' },
  { label: 'Suspend', count: 3, color: '#ff4d4f' },
  { label: 'Removed', count: 0, color: '#ff4d4f' }
];

const QualityDashboardDemo4 = () => {
  const [activeTab, setActiveTab] = useState('listings');
  const [compareMoM, setCompareMoM] = useState(true);
  const [dateRange, setDateRange] = useState(null);
  const [hostFilter, setHostFilter] = useState('');
  const [listingFilter, setListingFilter] = useState('');
  const [countryFilter, setCountryFilter] = useState('');
  const [cityFilter, setCityFilter] = useState('');
  const [qualityStatusFilter, setQualityStatusFilter] = useState('ALL');
  const [filtersVisible, setFiltersVisible] = useState(false);
  const [selectMetricsVisible, setSelectMetricsVisible] = useState(false);
  const [visibleListingsMetrics, setVisibleListingsMetrics] = useState([true, true, true, true]);
  const [issuesModalVisible, setIssuesModalVisible] = useState(false);
  const [selectedListingForModal, setSelectedListingForModal] = useState(null);
  const [showReviewTags, setShowReviewTags] = useState(false);

  const clearFilters = () => {
    setDateRange(null);
    setHostFilter('');
    setListingFilter('');
    setCountryFilter('');
    setCityFilter('');
    setQualityStatusFilter('ALL');
  };

  const removeFilter = key => {
    if (key === 'dateRange') setDateRange(null);
    if (key === 'host') setHostFilter('');
    if (key === 'listing') setListingFilter('');
    if (key === 'country') setCountryFilter('');
    if (key === 'city') setCityFilter('');
    if (key === 'qualityStatus') setQualityStatusFilter('ALL');
  };

  const activeFilterTags = [];
  if (dateRange && dateRange[0] && dateRange[1]) {
    activeFilterTags.push({
      key: 'dateRange',
      label: `From: ${moment(dateRange[0]).format('YYYY-MM-DD')} To: ${moment(dateRange[1]).format('YYYY-MM-DD')}`
    });
  }
  if (qualityStatusFilter && qualityStatusFilter !== 'ALL') activeFilterTags.push({ key: 'qualityStatus', label: `Status: ${qualityStatusFilter}` });
  if (hostFilter.trim()) activeFilterTags.push({ key: 'host', label: `Host: ${hostFilter.trim()}` });
  if (listingFilter.trim()) activeFilterTags.push({ key: 'listing', label: `Listing: ${listingFilter.trim()}` });
  if (countryFilter.trim()) activeFilterTags.push({ key: 'country', label: `Country: ${countryFilter.trim()}` });
  if (cityFilter.trim()) activeFilterTags.push({ key: 'city', label: `City: ${cityFilter.trim()}` });
  const hasActiveFilters = activeFilterTags.length > 0;

  useEffect(() => {
    window.dispatchEvent(new Event('resize'));
  }, [activeTab]);

  const listingsColumns = [
    {
      title: 'Listing name',
      dataIndex: 'listingName',
      key: 'listingName',
      width: 220,
      render: text => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ width: 32, height: 32, background: '#e8e8e8', borderRadius: 4, flexShrink: 0 }} />
          <span>{text}</span>
        </div>
      )
    },
    {
      title: 'Average review score',
      key: 'avgScore',
      width: 160,
      render: (_, r) => (
        <span>
          {r.avgScore} ({r.reviewCount})
        </span>
      )
    },
    {
      title: 'Airbnb quality status',
      dataIndex: 'qualityStatus',
      key: 'qualityStatus',
      width: 160,
      render: (_, r) => <Tag color={r.qualityStatusColor}>{r.qualityStatus}</Tag>
    },
    {
      title: 'Airbnb review issues',
      dataIndex: 'reviewIssues',
      key: 'reviewIssues',
      width: 140,
      render: (val, record) =>
        record.issuesTooltip ? (
          <Tooltip title={<pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontSize: 12 }}>{record.issuesTooltip}</pre>}>
            <span style={{ cursor: 'pointer', textDecoration: 'underline' }}>{val}</span>
          </Tooltip>
        ) : (
          val
        )
    },
    { title: 'Airbnb CS violations', dataIndex: 'csViolations', key: 'csViolations', width: 140 },
    {
      title: '',
      key: 'action',
      width: 100,
      render: (_, record) => (
        <Button
          type="link"
          size="small"
          style={{ padding: 0 }}
          onClick={() => {
            setSelectedListingForModal(record);
            setIssuesModalVisible(true);
          }}
        >
          Read more
        </Button>
      )
    }
  ];

  const maxViolation = Math.max(...violationsData.map(v => v.count));
  const accommodationPieData = accommodationStatuses.filter(s => s.count > 0).map(s => ({ type: s.label, count: s.count, color: s.color }));

  const { DataView } = DataSet;
  const accommodationDv = new DataView();
  accommodationDv.source(accommodationPieData).transform({
    type: 'percent',
    field: 'count',
    dimension: 'type',
    as: 'percent'
  });
  const accommodationCols = {
    percent: {
      formatter: val => (Number(val) * 100).toFixed(2) + '%'
    }
  };

  return (
    <StyledCard
      title={
        <span>
          Airbnb Quality Dashboard (Demo 4 — Reviews with tags){' '}
          <a
            href="https://youtu.be/4iOAk92hDpQ?t=157"
            target="_blank"
            rel="noopener noreferrer"
            style={{ fontWeight: 400, fontSize: 12, marginLeft: 8 }}
          >
            Reference Link - Avantio
          </a>
        </span>
      }
    >
      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        {/* ---------- Listings Quality (same as Demo 3) ---------- */}
        <Tabs.TabPane tab="Listings Quality" key="listings">
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 8,
              marginBottom: 16,
              padding: '12px 0',
              borderBottom: '1px solid #f0f0f0'
            }}
          >
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
              <Button icon="filter" onClick={() => setFiltersVisible(true)}>
                Filters
                {hasActiveFilters && (
                  <Tag color="blue" style={{ marginLeft: 6 }}>
                    {activeFilterTags.length}
                  </Tag>
                )}
              </Button>
              {activeFilterTags.map(({ key, label }) => (
                <Tag key={key} closable onClose={() => removeFilter(key)} color="blue">
                  {label}
                </Tag>
              ))}
              {hasActiveFilters && (
                <a onClick={clearFilters} style={{ fontSize: 13 }}>
                  Clear filters
                </a>
              )}
            </div>
            <Button type="primary" icon="download">
              Download report
            </Button>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
            <SectionTitle style={{ marginBottom: 0 }}>Metrics</SectionTitle>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {compareMoM && (
                <Tag closable onClose={() => setCompareMoM(false)} color="blue">
                  Compare: MoM
                </Tag>
              )}
              <a style={{ fontSize: 13 }} onClick={() => setSelectMetricsVisible(true)}>
                Select metrics ({visibleListingsMetrics.filter(Boolean).length})
              </a>
            </div>
          </div>
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            {visibleListingsMetrics[0] && (
              <Col xs={24} sm={12} md={6}>
                <KPICard>
                  <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>Listings in "Good" status</div>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                    <span style={{ fontSize: 22, fontWeight: 600, color: '#52c41a' }}>230 / 410</span>
                    <Icon type="arrow-up" style={{ color: '#1890ff', fontSize: 14 }} />
                  </div>
                </KPICard>
              </Col>
            )}
            {visibleListingsMetrics[1] && (
              <Col xs={24} sm={12} md={6}>
                <KPICard>
                  <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>Listings in "Warning" status</div>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                    <span style={{ fontSize: 22, fontWeight: 600, color: '#fa8c16' }}>40 / 410</span>
                    <Icon type="arrow-up" style={{ color: '#ff4d4f', fontSize: 14 }} />
                  </div>
                </KPICard>
              </Col>
            )}
            {visibleListingsMetrics[2] && (
              <Col xs={24} sm={12} md={6}>
                <KPICard>
                  <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>Listings at risk</div>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                    <span style={{ fontSize: 22, fontWeight: 600, color: '#ff4d4f' }}>30 / 410</span>
                    <Icon type="arrow-up" style={{ color: '#ff4d4f', fontSize: 14 }} />
                  </div>
                </KPICard>
              </Col>
            )}
            {visibleListingsMetrics[3] && (
              <Col xs={24} sm={12} md={6}>
                <KPICard>
                  <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>Suspended Listings</div>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                    <span style={{ fontSize: 22, fontWeight: 600, color: '#fa8c16' }}>10 / 410</span>
                    <Icon type="arrow-up" style={{ color: '#ff4d4f', fontSize: 14 }} />
                  </div>
                </KPICard>
              </Col>
            )}
          </Row>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <SectionTitle style={{ marginBottom: 0 }}>Listings</SectionTitle>
          </div>
          <Table columns={listingsColumns} dataSource={listingsData} pagination={{ pageSize: 10 }} size="small" />
        </Tabs.TabPane>

        {/* ---------- Reviews Quality (screenshot layout + Show/Hide review tags) ---------- */}
        <Tabs.TabPane tab="Reviews Quality" key="reviews">
          <div style={{ marginBottom: 24 }}>
            {/* Reviews section */}
            <SectionTitle style={{ marginBottom: 16 }}>Reviews</SectionTitle>
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: 24, marginBottom: 24 }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 8 }}>
                <span style={{ fontSize: 36, fontWeight: 600, color: '#262626' }}>★ 4.48</span>
                <Button
                  type="default"
                  size="small"
                  style={{ borderColor: '#ff4d4f', color: '#ff4d4f' }}
                  onClick={() => setShowReviewTags(!showReviewTags)}
                >
                  {showReviewTags ? 'Hide review tags' : 'Show review tags'}
                </Button>
              </div>
              <Row gutter={[16, 16]} style={{ flex: 1, minWidth: 0 }}>
                {reviewCategories.map(cat => (
                  <Col xs={24} sm={12} md={8} lg={6} key={cat.key}>
                    <Card size="small" style={{ borderRadius: 8, border: '1px solid #e8e8e8', position: 'relative' }}>
                      <Tooltip title="Category score and distribution">
                        <Icon type="info-circle" style={{ position: 'absolute', top: 8, right: 8, color: '#8c8c8c', fontSize: 14 }} />
                      </Tooltip>
                      <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>{cat.label}</div>
                      <div style={{ fontSize: 18, fontWeight: 600, color: '#262626', marginBottom: 8 }}>★ {cat.rating}</div>
                      <div style={{ display: 'flex', height: 8, borderRadius: 4, overflow: 'hidden', background: '#f0f0f0' }}>
                        <div style={{ width: `${cat.greenPct}%`, background: '#52c41a' }} />
                        <div style={{ width: `${cat.redPct}%`, background: '#ff4d4f' }} />
                      </div>
                      <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: 4 }}>
                        <span style={{ color: '#52c41a' }}>{cat.greenPct}%</span>
                        <span style={{ margin: '0 4px' }}>/</span>
                        <span style={{ color: '#ff4d4f' }}>{cat.redPct}%</span>
                      </div>
                    </Card>
                  </Col>
                ))}
              </Row>
            </div>

            {/* Review Tags section - only when showReviewTags is true */}
            {showReviewTags && (
              <div style={{ marginBottom: 24, padding: 16, background: '#fafafa', borderRadius: 8, border: '1px solid #e8e8e8' }}>
                <SectionTitle style={{ marginBottom: 16 }}>Review Tags</SectionTitle>
                <Row gutter={[16, 16]}>
                  {reviewCategories.map(cat => {
                    const data = reviewTagsByCategory[cat.key];
                    if (!data) return null;
                    return (
                      <Col xs={24} sm={12} md={8} key={cat.key}>
                        <Card
                          size="small"
                          style={{
                            borderRadius: 8,
                            border: '1px solid #e8e8e8',
                            height: 320
                          }}
                          bodyStyle={{ height: 288, display: 'flex', flexDirection: 'column', padding: 16, overflow: 'hidden' }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12, flexShrink: 0 }}>
                            <Icon type="message" style={{ color: '#1890ff' }} />
                            <span style={{ fontWeight: 600 }}>{cat.label}</span>
                            <span style={{ fontSize: 12, color: '#8c8c8c' }}>({data.total})</span>
                          </div>
                          <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
                            <div style={{ marginBottom: 8 }}>
                              <div style={{ fontSize: 11, color: '#8c8c8c', marginBottom: 4 }}>Positive</div>
                              {(data.positive || []).map((p, i) => (
                                <div key={i} style={{ fontSize: 13, color: '#52c41a', marginBottom: 2 }}>
                                  {p.tag} <span style={{ color: '#8c8c8c' }}>({p.count})</span>
                                </div>
                              ))}
                            </div>
                            <div>
                              <div style={{ fontSize: 11, color: '#8c8c8c', marginBottom: 4 }}>Negative</div>
                              {(data.negative || []).map((n, i) => (
                                <div key={i} style={{ fontSize: 13, color: '#ff4d4f', marginBottom: 2 }}>
                                  {n.tag} <span style={{ color: '#8c8c8c' }}>({n.count})</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        </Card>
                      </Col>
                    );
                  })}
                </Row>
              </div>
            )}

            {/* Violations + Accommodation status side by side */}
            <Row gutter={24}>
              <Col xs={24} md={12}>
                <div style={{ marginBottom: 16 }}>
                  <SectionTitle style={{ marginBottom: 12 }}>Violations</SectionTitle>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                    <span style={{ fontSize: 32, fontWeight: 600, color: '#ff4d4f' }}>103</span>
                    <Icon type="safety" style={{ fontSize: 28, color: '#ff4d4f' }} />
                  </div>
                  {violationsData.map((v, idx) => (
                    <div key={idx} style={{ marginBottom: 12 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                        <span style={{ fontSize: 13 }}>
                          <Icon type={v.icon} style={{ marginRight: 6 }} />
                          {v.label}
                        </span>
                        <span style={{ fontWeight: 600 }}>{v.count}</span>
                      </div>
                      <Progress
                        percent={maxViolation ? Math.round((v.count / maxViolation) * 100) : 0}
                        showInfo={false}
                        strokeColor="#ff4d4f"
                        size="small"
                      />
                    </div>
                  ))}
                </div>
              </Col>
              <Col xs={24} md={12}>
                <div>
                  <SectionTitle style={{ marginBottom: 8 }}>Accommodation status</SectionTitle>
                  <p style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 12 }}>
                    Review the trip issues reported by your guests by hovering over each category
                  </p>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {accommodationStatuses.map((s, idx) => (
                      <Tag key={idx} color={s.color} style={{ marginBottom: 4 }}>
                        {s.label} ({s.count})
                      </Tag>
                    ))}
                  </div>
                  <div style={{ marginTop: 16, width: '100%', maxWidth: 420, height: 340 }}>
                    {accommodationPieData.length > 0 ? (
                      <Chart data={accommodationDv} scale={accommodationCols} forceFit height={340}>
                        <Coord type="theta" radius={0.75} innerRadius={0.6} />
                        <Axis name="percent" />
                        <Geom
                          type="intervalStack"
                          position="percent"
                          color={[
                            'type',
                            type => {
                              const match = accommodationPieData.find(d => d.type === type);
                              return (match && match.color) || '#999';
                            }
                          ]}
                          style={{ lineWidth: 1, stroke: '#fff' }}
                          tooltip={[
                            'type*percent',
                            type => {
                              const match = accommodationPieData.find(d => d.type === type);
                              return {
                                name: type,
                                value: match && match.count != null ? match.count : 0
                              };
                            }
                          ]}
                        >
                          <Label content="percent" formatter={(val, item) => item.point.type} />
                        </Geom>
                        <ChartTooltip showTitle={false} />
                      </Chart>
                    ) : (
                      <div
                        style={{
                          width: 120,
                          height: 120,
                          borderRadius: '50%',
                          border: '3px solid #e8e8e8',
                          background: '#fafafa',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: 12,
                          color: '#8c8c8c'
                        }}
                      >
                        No data
                      </div>
                    )}
                  </div>
                </div>
              </Col>
            </Row>
          </div>
        </Tabs.TabPane>
      </Tabs>

      <Modal
        title="Filters"
        visible={filtersVisible}
        onCancel={() => setFiltersVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setFiltersVisible(false)}>
            Cancel
          </Button>,
          <Button key="apply" type="primary" onClick={() => setFiltersVisible(false)}>
            Apply
          </Button>
        ]}
      >
        <div style={{ marginBottom: 12 }}>
          <div style={{ marginBottom: 4, fontSize: 12, color: '#666' }}>Date Range</div>
          <RangePicker value={dateRange} onChange={setDateRange} style={{ width: '100%' }} />
        </div>
        <div style={{ marginBottom: 12 }}>
          <div style={{ marginBottom: 4, fontSize: 12, color: '#666' }}>Host name / ID</div>
          <Input placeholder="e.g. Property Manager A" value={hostFilter} onChange={e => setHostFilter(e.target.value)} />
        </div>
        <div style={{ marginBottom: 12 }}>
          <div style={{ marginBottom: 4, fontSize: 12, color: '#666' }}>Listing name / ID</div>
          <Input placeholder="e.g. Seaside View Apartment" value={listingFilter} onChange={e => setListingFilter(e.target.value)} />
        </div>
        <div style={{ marginBottom: 12 }}>
          <div style={{ marginBottom: 4, fontSize: 12, color: '#666' }}>Country</div>
          <Input placeholder="e.g. Italy" value={countryFilter} onChange={e => setCountryFilter(e.target.value)} />
        </div>
        <div style={{ marginBottom: 12 }}>
          <div style={{ marginBottom: 4, fontSize: 12, color: '#666' }}>City</div>
          <Input placeholder="e.g. Napoli" value={cityFilter} onChange={e => setCityFilter(e.target.value)} />
        </div>
        <div style={{ marginBottom: 12 }}>
          <div style={{ marginBottom: 4, fontSize: 12, color: '#666' }}>Latest Quality Status</div>
          <Select value={qualityStatusFilter} onChange={setQualityStatusFilter} style={{ width: '100%' }}>
            <Option value="ALL">ALL</Option>
            <Option value="Good">Good</Option>
            <Option value="Educate">Educate</Option>
            <Option value="Warn">Warn</Option>
            <Option value="Probation">Probation</Option>
            <Option value="Removed">Removed</Option>
          </Select>
        </div>
      </Modal>

      <Modal
        title="Select metrics"
        visible={selectMetricsVisible}
        onCancel={() => setSelectMetricsVisible(false)}
        footer={
          <Button type="primary" onClick={() => setSelectMetricsVisible(false)}>
            OK
          </Button>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {LISTINGS_METRIC_LABELS.map((label, idx) => (
            <Checkbox
              key={idx}
              checked={visibleListingsMetrics[idx]}
              onChange={e => {
                const next = [...visibleListingsMetrics];
                next[idx] = e.target.checked;
                setVisibleListingsMetrics(next);
              }}
            >
              {label}
            </Checkbox>
          ))}
        </div>
      </Modal>

      <Modal
        title="Airbnb issues, cancellations and violations"
        visible={issuesModalVisible}
        onCancel={() => setIssuesModalVisible(false)}
        footer={
          <Button type="primary" onClick={() => setIssuesModalVisible(false)}>
            Ok
          </Button>
        }
        width={560}
      >
        {selectedListingForModal && (
          <>
            {(selectedListingForModal.modalReviewIssues || []).length > 0 && (
              <div style={{ marginBottom: 24 }}>
                <div style={{ fontWeight: 600, marginBottom: 12, color: '#262626' }}>Airbnb review issues</div>
                {(selectedListingForModal.modalReviewIssues || []).map((item, idx) => (
                  <div key={idx} style={{ marginBottom: 16 }}>
                    <div style={{ marginBottom: 4 }}>
                      <span style={{ marginRight: 8 }}>Reservation ID: {item.reservationId}</span>
                      <Button type="link" size="small" style={{ padding: 0, fontSize: 13 }}>
                        Review information
                      </Button>
                    </div>
                    <ul style={{ margin: 0, paddingLeft: 20, fontSize: 13, color: '#595959' }}>
                      {(item.issues || []).map((issue, i) => (
                        <li key={i}>{issue}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}
            {(selectedListingForModal.modalViolations || []).length > 0 && (
              <div>
                <div style={{ fontWeight: 600, marginBottom: 12, color: '#262626' }}>Airbnb violations</div>
                {(selectedListingForModal.modalViolations || []).map((item, idx) => (
                  <div key={idx} style={{ marginBottom: 16 }}>
                    <div style={{ marginBottom: 4 }}>
                      <span style={{ marginRight: 8 }}>Reservation ID: {item.reservationId}</span>
                      <Button type="link" size="small" style={{ padding: 0, fontSize: 13 }}>
                        Review information
                      </Button>
                    </div>
                    <ul style={{ margin: 0, paddingLeft: 20, fontSize: 13, color: '#595959' }}>
                      {(item.violations || []).map((v, i) => (
                        <li key={i}>{v}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}
            {(selectedListingForModal.modalReviewIssues || []).length === 0 && (selectedListingForModal.modalViolations || []).length === 0 && (
              <div style={{ color: '#8c8c8c', fontSize: 13 }}>No issues or violations for this listing.</div>
            )}
          </>
        )}
      </Modal>
    </StyledCard>
  );
};

export default QualityDashboardDemo4;
